import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { 
  Plus,
  Calendar,
  User,
  Clock,
  MapPin,
  CheckCircle,
  AlertCircle,
  Edit,
  Trash2,
  Play,
  Pause
} from 'lucide-react'
import { apiClient } from '../config/api'

interface Task {
  id?: number
  task_name: string
  assigned_staff_id: string
  assigned_staff_name: string
  additional_staff_ids?: string[]
  assigned_vehicle_ids?: string[]
  related_alert_ids?: string[]
  start_date: string
  end_date: string
  duration_hours: number
  location: string
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'DELAYED'
  progress: number
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'
  notes: string
}

interface TaskManagerProps {
  missionId: string
  missionName: string
  missionStaff: any[] // Staff assigned to this specific mission
  missionVehicles?: any[] // Vehicles assigned to this specific mission
  missionAlerts?: any[] // Alerts assigned to this specific mission
  onClose: () => void
  showMessage?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

const TaskManager: React.FC<TaskManagerProps> = ({ 
  missionId, 
  missionName, 
  missionStaff,
  missionVehicles = [],
  missionAlerts = [],
  onClose,
  showMessage 
}) => {
  const { t } = useTranslation()
  const [tasks, setTasks] = useState<Task[]>([])
  const [showTaskForm, setShowTaskForm] = useState(false)
  const [editingTask, setEditingTask] = useState<Task | null>(null)
  const [viewMode, setViewMode] = useState<'list' | 'gantt' | 'calendar'>('list')
  
  // Load data like in mission creation
  const [allStaff, setAllStaff] = useState<any[]>([])
  const [allVehicles, setAllVehicles] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  
  const [newTask, setNewTask] = useState<Task>({
    task_name: '',
    assigned_staff_id: '',
    assigned_staff_name: '',
    additional_staff_ids: [],
    assigned_vehicle_ids: [],
    related_alert_ids: [],
    start_date: '',
    end_date: '',
    duration_hours: 1,
    location: '',
    status: 'PENDING',
    progress: 0,
    priority: 'MEDIUM',
    notes: ''
  })

  // Load all data like in mission creation
  useEffect(() => {
    loadTaskFormData()
  }, [])

  const loadTaskFormData = async () => {
    try {
      setLoading(true)
      console.log('🔄 Loading task form data (like mission creation)...')

      // Load all staff
      try {
        const staffResponse = await apiClient.get('/staff?employment_status=ACTIVE')
        const staffData = staffResponse.data || staffResponse
        const staffArray = Array.isArray(staffData) ? staffData : []
        setAllStaff(staffArray)
        console.log('✅ Loaded all staff:', staffArray.length)
      } catch (error) {
        console.error('❌ Failed to load staff:', error)
        setAllStaff([])
      }

      // Load all vehicles
      try {
        const vehiclesResponse = await apiClient.get('/vehicles')
        const vehiclesData = vehiclesResponse.data || vehiclesResponse
        const vehiclesArray = Array.isArray(vehiclesData) ? vehiclesData : []
        setAllVehicles(vehiclesArray)
        console.log('✅ Loaded all vehicles:', vehiclesArray.length)
      } catch (error) {
        console.error('❌ Failed to load vehicles:', error)
        setAllVehicles([])
      }

    } catch (error) {
      console.error('❌ Failed to load task form data:', error)
    } finally {
      setLoading(false)
    }
  }

  // Get mission-assigned staff from all staff
  const getMissionAssignedStaff = () => {
    if (!Array.isArray(allStaff) || allStaff.length === 0) return []
    
    // Extract staff IDs from mission (similar to mission creation logic)
    const missionStaffIds = missionStaff.map(s => s.id?.toString() || s.staff_info?.id?.toString()).filter(Boolean)
    
    // Filter all staff to only show mission-assigned ones
    const assignedStaff = allStaff.filter(staff => 
      missionStaffIds.includes(staff.id?.toString())
    )
    
    console.log('🔍 Mission assigned staff:', {
      missionStaffIds,
      foundStaff: assignedStaff.length,
      staffNames: assignedStaff.map(s => s.full_name)
    })
    
    return assignedStaff
  }

  // Get mission-assigned vehicles from all vehicles
  const getMissionAssignedVehicles = () => {
    if (!Array.isArray(allVehicles) || allVehicles.length === 0) return []
    
    // Extract vehicle IDs from mission
    const missionVehicleIds = missionVehicles.map(v => v.id?.toString()).filter(Boolean)
    
    // Filter all vehicles to only show mission-assigned ones
    const assignedVehicles = allVehicles.filter(vehicle => 
      missionVehicleIds.includes(vehicle.id?.toString())
    )
    
    console.log('🔍 Mission assigned vehicles:', {
      missionVehicleIds,
      foundVehicles: assignedVehicles.length,
      vehicleNames: assignedVehicles.map(v => v.custom_name || v.vehicle_name)
    })
    
    return assignedVehicles
  }

  const addTask = () => {
    if (!newTask.task_name || !newTask.assigned_staff_id) {
      showMessage?.('Please fill in task name and assign staff', 'warning')
      return
    }

    const staffMember = missionStaff.find(s => s.id.toString() === newTask.assigned_staff_id)
    const task: Task = {
      ...newTask,
      id: Date.now(),
      assigned_staff_name: staffMember?.full_name || 'Unknown Staff'
    }

    setTasks([...tasks, task])
    setNewTask({
      task_name: '',
      assigned_staff_id: '',
      assigned_staff_name: '',
      start_date: '',
      end_date: '',
      duration_hours: 1,
      location: '',
      status: 'PENDING',
      progress: 0,
      priority: 'MEDIUM',
      notes: ''
    })
    setShowTaskForm(false)
    showMessage?.(`Task "${task.task_name}" created`, 'success')
  }

  const updateTaskStatus = (taskId: number, newStatus: string) => {
    setTasks(tasks.map(task => 
      task.id === taskId 
        ? { 
            ...task, 
            status: newStatus as any,
            progress: newStatus === 'COMPLETED' ? 100 : newStatus === 'IN_PROGRESS' ? 50 : 0
          }
        : task
    ))
    showMessage?.('Task status updated', 'success')
  }

  const deleteTask = (taskId: number) => {
    setTasks(tasks.filter(task => task.id !== taskId))
    showMessage?.('Task deleted', 'success')
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'bg-green-100 text-green-800'
      case 'IN_PROGRESS': return 'bg-blue-100 text-blue-800'
      case 'DELAYED': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT': return 'bg-red-200 text-red-800'
      case 'HIGH': return 'bg-orange-200 text-orange-800'
      case 'MEDIUM': return 'bg-yellow-200 text-yellow-800'
      case 'LOW': return 'bg-green-200 text-green-800'
      default: return 'bg-gray-200 text-gray-800'
    }
  }

  // Simple Gantt Chart View
  const renderGanttView = () => {
    const today = new Date()
    const startOfWeek = new Date(today.setDate(today.getDate() - today.getDay()))
    const days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(startOfWeek)
      date.setDate(startOfWeek.getDate() + i)
      return date
    })

    return (
      <div className="p-4">
        <h3 className="text-lg font-semibold mb-4">{t('gantt_chart')}</h3>
        
        {/* Timeline Header */}
        <div className="grid grid-cols-8 gap-2 mb-4">
          <div className="font-medium text-sm">{t('task')}</div>
          {days.map(day => (
            <div key={day.toISOString()} className="text-center text-xs font-medium text-gray-600">
              {day.toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric' })}
            </div>
          ))}
        </div>

        {/* Task Rows */}
        <div className="space-y-2">
          {tasks.map(task => (
            <div key={task.id} className="grid grid-cols-8 gap-2 items-center">
              <div className="text-sm font-medium truncate">
                {task.task_name}
                <div className="text-xs text-gray-500">{task.assigned_staff_name}</div>
              </div>
              
              {days.map(day => {
                const taskStart = task.start_date ? new Date(task.start_date) : null
                const taskEnd = task.end_date ? new Date(task.end_date) : null
                const isTaskDay = taskStart && taskEnd && 
                  day >= taskStart && day <= taskEnd
                
                return (
                  <div key={day.toISOString()} className="h-8 relative">
                    {isTaskDay && (
                      <div className={`absolute inset-0 rounded ${getStatusColor(task.status)} opacity-75 flex items-center justify-center`}>
                        <div className="text-xs font-medium">{task.progress}%</div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-4 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[85vh] flex flex-col">
        {/* Mac-style Header */}
        <div className="bg-gradient-to-b from-slate-700 to-slate-900 text-white px-6 py-4 flex items-center justify-between rounded-t-lg flex-shrink-0">
          <h2 className="text-lg font-semibold">{t('task_management')}</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200"
          >
            ×
          </button>
        </div>
        
        <div className="flex-1 bg-white flex flex-col min-h-0">
          <div className="mx-6 mt-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
            {t('tasks_not_persisted', {
              defaultValue: 'Tasks are session-only for now — they are not saved to the server. Closing this window discards them.'
            })}
          </div>
          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{missionName}</p>
                <p className="text-xs text-gray-500">
                  Resources: {getMissionAssignedStaff().length} staff, {getMissionAssignedVehicles().length} vehicles
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                {/* View Mode Selector */}
                <div className="flex bg-gray-200 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-3 py-1 text-xs rounded ${viewMode === 'list' ? 'bg-white shadow' : ''}`}
                  >
                    {t('list_view')}
                  </button>
                  <button
                    onClick={() => setViewMode('gantt')}
                    className={`px-3 py-1 text-xs rounded ${viewMode === 'gantt' ? 'bg-white shadow' : ''}`}
                  >
                    {t('gantt_view')}
                  </button>
                </div>
                
                <button
                  onClick={() => setShowTaskForm(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t('add_task')}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {viewMode === 'gantt' ? renderGanttView() : (
              /* List View */
              <div className="p-6 overflow-y-auto">
                {tasks.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{t('no_tasks')}</h3>
                    <p className="text-gray-600 mb-4">{t('no_tasks_desc')}</p>
                    <button
                      onClick={() => setShowTaskForm(true)}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      {t('create_first_task')}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {tasks.map(task => (
                      <div key={task.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h4 className="font-semibold text-gray-900">{task.task_name}</h4>
                              <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(task.status)}`}>
                                {task.status}
                              </span>
                              <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(task.priority)}`}>
                                {task.priority}
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-4 text-sm text-gray-600">
                              <div className="flex items-center space-x-1">
                                <User className="w-3 h-3" />
                                <span>{task.assigned_staff_name}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Clock className="w-3 h-3" />
                                <span>{task.duration_hours}h</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <MapPin className="w-3 h-3" />
                                <span>{task.location || 'No location'}</span>
                              </div>
                            </div>

                            {/* Progress Bar */}
                            <div className="mt-3">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs text-gray-500">{t('progress')}</span>
                                <span className="text-xs text-gray-600">{task.progress}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-blue-600 h-2 rounded-full transition-all"
                                  style={{ width: `${task.progress}%` }}
                                />
                              </div>
                            </div>
                          </div>
                          
                          {/* Task Actions */}
                          <div className="flex items-center space-x-2 ml-4">
                            {task.status === 'PENDING' && (
                              <button
                                onClick={() => updateTaskStatus(task.id!, 'IN_PROGRESS')}
                                className="p-2 text-green-600 hover:bg-green-50 rounded"
                                title={t('start_task')}
                              >
                                <Play className="w-4 h-4" />
                              </button>
                            )}
                            
                            {task.status === 'IN_PROGRESS' && (
                              <button
                                onClick={() => updateTaskStatus(task.id!, 'COMPLETED')}
                                className="p-2 text-green-600 hover:bg-green-50 rounded"
                                title={t('complete_task')}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                            
                            <button
                              onClick={() => {
                                setEditingTask(task)
                                setNewTask(task)
                                setShowTaskForm(true)
                              }}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                              title={t('edit_task')}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            
                            <button
                              onClick={() => deleteTask(task.id!)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                              title={t('delete_task')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Simple Task Creation Form */}
          {showTaskForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[300]">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
                <div className="bg-blue-600 text-white px-6 py-4 flex items-center justify-between flex-shrink-0">
                  <h3 className="text-lg font-semibold">
                    {editingTask ? t('edit_task') : t('create_new_task')}
                  </h3>
                  <button
                    onClick={() => {
                      setShowTaskForm(false)
                      setEditingTask(null)
                      setNewTask({
                        task_name: '',
                        assigned_staff_id: '',
                        assigned_staff_name: '',
                        start_date: '',
                        end_date: '',
                        duration_hours: 1,
                        location: '',
                        status: 'PENDING',
                        progress: 0,
                        priority: 'MEDIUM',
                        notes: ''
                      })
                    }}
                    className="text-white hover:text-gray-200"
                  >
                    ×
                  </button>
                </div>
                
                <div className="p-6 space-y-4 overflow-y-auto flex-1">
                  {/* Task Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('task_name')} *</label>
                    <input
                      type="text"
                      value={newTask.task_name}
                      onChange={(e) => setNewTask({...newTask, task_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Load containers, Transport to port, etc."
                    />
                  </div>

                  {/* Staff Assignment - Like Mission Creation */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Assign Staff Members *
                      <span className="text-xs text-blue-600 ml-2">
                        (From mission assigned staff)
                      </span>
                    </label>
                    
                    {/* Primary Staff Selection */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-600 mb-2">Primary Responsible Staff:</label>
                      <select
                        value={newTask.assigned_staff_id}
                        onChange={(e) => {
                          const assignedStaff = getMissionAssignedStaff()
                          const staffMember = assignedStaff.find(s => s.id?.toString() === e.target.value)
                          setNewTask({
                            ...newTask, 
                            assigned_staff_id: e.target.value,
                            assigned_staff_name: staffMember?.full_name || ''
                          })
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select primary staff...</option>
                        {getMissionAssignedStaff().map((staffMember) => (
                          <option key={staffMember.id} value={staffMember.id}>
                            {staffMember.full_name} - {staffMember.position || staffMember.staff_type} ({staffMember.company_name || 'Internal'})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Additional Staff Selection */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-600 mb-2">Additional Staff (Optional):</label>
                      <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                        {getMissionAssignedStaff().length > 0 ? (
                          getMissionAssignedStaff().map((staffMember) => (
                            <div key={staffMember.id} className="flex items-center space-x-3 py-1">
                              <input
                                type="checkbox"
                                id={`additional-staff-${staffMember.id}`}
                                checked={newTask.additional_staff_ids?.includes(staffMember.id.toString()) || false}
                                onChange={(e) => {
                                  const currentIds = newTask.additional_staff_ids || []
                                  const staffId = staffMember.id.toString()
                                  const newIds = e.target.checked
                                    ? [...currentIds, staffId]
                                    : currentIds.filter(id => id !== staffId)
                                  setNewTask({
                                    ...newTask,
                                    additional_staff_ids: newIds
                                  })
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <label htmlFor={`additional-staff-${staffMember.id}`} className="flex-1 text-sm cursor-pointer">
                                <span className="font-medium">{staffMember.full_name}</span>
                                <span className="text-gray-600"> - {staffMember.position || staffMember.staff_type}</span>
                                <span className="text-gray-500"> ({staffMember.company_name || 'Internal'})</span>
                                {newTask.assigned_staff_id === staffMember.id.toString() && (
                                  <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">PRIMARY</span>
                                )}
                              </label>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-500 text-sm">No staff assigned to this mission</p>
                        )}
                      </div>
                    </div>

                    <div className="text-xs text-gray-500">
                      Total staff assigned: {1 + (newTask.additional_staff_ids?.length || 0)} 
                      ({newTask.assigned_staff_id ? 1 : 0} primary + {newTask.additional_staff_ids?.length || 0} additional)
                    </div>
                  </div>

                  {/* Vehicle Assignment - Like Mission Creation */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Vehicle Assignment
                      <span className="text-xs text-blue-600 ml-2">
                        (From mission assigned vehicles)
                      </span>
                    </label>

                    {/* Vehicle Selection */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-600 mb-2">Select Vehicles (Optional)</label>
                      <div className="text-xs text-gray-500 mb-2">
                        ({getMissionAssignedVehicles().length} vehicles available from mission)
                      </div>
                      
                      {/* Select All / Clear All */}
                      <div className="flex space-x-2 mb-3">
                        <button
                          type="button"
                          onClick={() => {
                            const allVehicleIds = getMissionAssignedVehicles().map(v => v.id.toString())
                            setNewTask({
                              ...newTask,
                              assigned_vehicle_ids: allVehicleIds
                            })
                          }}
                          className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                        >
                          {t('select_all')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewTask({...newTask, assigned_vehicle_ids: []})}
                          className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                        >
                          {t('clear_all')}
                        </button>
                      </div>

                      {/* Vehicle List */}
                      <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50">
                        {getMissionAssignedVehicles().length > 0 ? (
                          getMissionAssignedVehicles().map((vehicle) => (
                            <div key={vehicle.id} className="flex items-center space-x-3 p-3 border-b border-gray-200 last:border-b-0 hover:bg-gray-100">
                              <input
                                type="checkbox"
                                id={`vehicle-${vehicle.id}`}
                                checked={newTask.assigned_vehicle_ids?.includes(vehicle.id.toString()) || false}
                                onChange={(e) => {
                                  const currentIds = newTask.assigned_vehicle_ids || []
                                  const vehicleId = vehicle.id.toString()
                                  const newIds = e.target.checked
                                    ? [...currentIds, vehicleId]
                                    : currentIds.filter(id => id !== vehicleId)
                                  setNewTask({
                                    ...newTask,
                                    assigned_vehicle_ids: newIds
                                  })
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <label htmlFor={`vehicle-${vehicle.id}`} className="flex-1 cursor-pointer">
                                <div className="font-medium text-gray-900">
                                  {vehicle.custom_name || vehicle.vehicle_name || `Vehicle ${vehicle.id}`}
                                </div>
                                <div className="text-sm text-gray-600">
                                  Plate: {vehicle.custom_plate_number || vehicle.license_plate || 'No Plate'} • 
                                  Customer: {vehicle.customer_name || 'Unknown'}
                                </div>
                              </label>
                            </div>
                          ))
                        ) : (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            No vehicles assigned to this mission
                          </div>
                        )}
                      </div>

                      <div className="text-xs text-gray-500 mt-2">
                        {newTask.assigned_vehicle_ids?.length || 0} vehicles selected
                      </div>
                    </div>
                  </div>

                  {/* Schedule */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('start_date')}</label>
                      <input
                        type="datetime-local"
                        value={newTask.start_date}
                        onChange={(e) => setNewTask({...newTask, start_date: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('end_date')}</label>
                      <input
                        type="datetime-local"
                        value={newTask.end_date}
                        onChange={(e) => setNewTask({...newTask, end_date: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('duration_hours')}</label>
                      <input
                        type="number"
                        value={newTask.duration_hours}
                        onChange={(e) => setNewTask({...newTask, duration_hours: parseInt(e.target.value) || 1})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        min="1"
                        max="24"
                      />
                    </div>
                  </div>

                  {/* Location and Priority */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                      <input
                        type="text"
                        value={newTask.location}
                        onChange={(e) => setNewTask({...newTask, location: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Task location..."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('priority')}</label>
                      <select
                        value={newTask.priority}
                        onChange={(e) => setNewTask({...newTask, priority: e.target.value as any})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="LOW">{t('low')}</option>
                        <option value="MEDIUM">{t('medium')}</option>
                        <option value="HIGH">{t('high')}</option>
                        <option value="URGENT">{t('urgent')}</option>
                      </select>
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                    <textarea
                      value={newTask.notes}
                      onChange={(e) => setNewTask({...newTask, notes: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="Task instructions or notes..."
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => {
                        setShowTaskForm(false)
                        setEditingTask(null)
                      }}
                      className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                    >
                      {t('cancel')}
                    </button>
                    <button
                      onClick={addTask}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      {editingTask ? t('update_task') : t('create_task')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TaskManager
